import { Add } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";

import {
  FindAllTicketsQueryFilters,
  FindAllTicketsQueryFiltersSortByEnum,
  FindAllTicketsQueryFiltersSortOrderEnum,
  FindAllTicketsQueryFiltersTicketStatusEnum,
  FindAllTicketsQueryFiltersTicketTypeEnum,
  TicketDto,
} from "../../api-client";
import useStore from "../../helpers/useStore";
import { TicketsStoreContext } from "../../stores/Ticketing/tickets.provider";
import { UserContext } from "../../stores/User/User.provider";
import { attentionColors, mobiliTheme } from "../../themes/mobiliTheme";
import { useTickets } from "../../stores/Ticketing";
import { useTicketFiltersFlateState } from "./hooks/useTicketFilterFlatState";
import TicketDialog from "./TicketDialog";
import TicketForm from "./TicketEditCreate/ticketForm";
import TicketIcon from "./TicketEditCreate/TicketIcon";
import TicketFilters from "./TicketFilters";
import TicketsTable from "./TicketsTable";
import BsnrTableToolbar from "./BsnrTableToolbar";
import CreateTicketForm from "./TicketEditCreate/createTicketForm";
import { TicketFlatFilterDto } from "../../api-client";

export const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "High":
    case "HIGH":
      return attentionColors.immediateAttention; // Set background color to red for High priority
    case "Medium":
    case "MEDIUM":
      return attentionColors.mediumPriority; // Set background color to yellow for Medium priority
    case "Low":
    case "LOW":
      return attentionColors.lowPriority; // Set background color to green for Low priority
    default:
      return attentionColors.customInfoPrimary; // Default background color
  }
};

const TicketOverview: React.FC = observer(() => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const TicketsStore = useStore(TicketsStoreContext);
  const [refresh, setRefresh] = useState(false);

  const UserStore = useStore(UserContext);
  const { user } = UserStore;

  const [openDialog, setOpenDialog] = useState(false);
  const [openTicket, setOpenTicket] = useState(false);
  // const commonFilter: FindAllTicketsQueryFilters = useMemo(
  //   () => ({
  //     limit: 10,
  //     page: 1,
  //     locationId: user?.locationId,
  //   }),
  //   [user?.locationId]
  // );
  const commonFilter: FindAllTicketsQueryFilters = {
    limit: 10,
    // page: 1,
    locationId: user?.locationId,
    ticketType: Object.values(FindAllTicketsQueryFiltersTicketTypeEnum).filter(
      (type) => type !== FindAllTicketsQueryFiltersTicketTypeEnum.OrderRx
    ),
  };
  const defaultFilter: FindAllTicketsQueryFilters = {
    ...commonFilter,
    sortBy: FindAllTicketsQueryFiltersSortByEnum.CreatedAt,
    sortOrder: FindAllTicketsQueryFiltersSortOrderEnum.Asc,
    ticketStatus: [FindAllTicketsQueryFiltersTicketStatusEnum.New],
  };

  const [ticketFilters, setTicketFilters] = useState<FindAllTicketsQueryFilters>(defaultFilter);

  // Initialize the ticket filters hook
  const {
    filterState,
    showFilters,
    updateType,
    updateStatus,
    updateFromDate,
    updateToDate,
    updateSort,
    resetFilters,
    toggleFilters,
    translatedFlatTicketFilters,
  } = useTicketFiltersFlateState({
    baseFilter: defaultFilter,
    onFiltersChange: (filters) => {
      // resetData();
      handleTabChange(null as any, selectedTab);
    },
  });

  const processFilterState = (
    state: typeof filterState,
    ticketFilters: FindAllTicketsQueryFilters
  ) => {
    // Create a new object instead of mutating the input
    const newFilters = { ...ticketFilters };
      // Only override ticketType if user selected something
  if (state.type && state.type.length > 0) {
    newFilters.ticketType = state.type as any;
  } else {
    // Keep the default exclusion of OrderRx
    newFilters.ticketType = Object.values(FindAllTicketsQueryFiltersTicketTypeEnum).filter(
      (type) => type !== FindAllTicketsQueryFiltersTicketTypeEnum.OrderRx
    );
  }


    if (state.status) {
      newFilters.ticketStatus = state.status as any;
    }

    if (state.fromDate) {
      newFilters.dateFrom = state.fromDate.toISOString();
    }

    if (state.toDate) {
      newFilters.dateTo = state.toDate.toISOString();
    }

    if (state.sortBy === "priority") {
      newFilters.sortBy = "priority";
      newFilters.sortOrder = state.sortOrder;
    } else if (state.sortBy === "BSNR") {
      // Sort/group by clinic id (backend maps the "BSNR" sort key to rx.clinicId).
      newFilters.sortBy = "BSNR";
      newFilters.sortOrder = state.sortOrder;
    } else {
      newFilters.sortBy = "createdAt";
      newFilters.sortOrder = state.sortOrder;
    }

    return newFilters;
  };

  const filterClosedAndDoneTicketStatus = (
    ticketStatus: FindAllTicketsQueryFiltersTicketStatusEnum[]
  ) => {
    // console.debug("Filtering closed and done from:", ticketStatus);
    // if (!ticketStatus?.length) return [];
    return ticketStatus.filter(
      (status) =>
        status !== FindAllTicketsQueryFiltersTicketStatusEnum.Closed &&
        status !== FindAllTicketsQueryFiltersTicketStatusEnum.ConversationDone
    );
  };

  // on TabCHhange: reset to tab specifc filters + apply filters from filterState
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    // resetData();
    switch (newValue) {
      case 0:
        const updatedFilters = {
          ...defaultFilter,
          ...processFilterState(filterState, defaultFilter),
          ticketStatus:
            newValue === 0 && (!filterState.status || filterState.status.length === 0)
              ? [FindAllTicketsQueryFiltersTicketStatusEnum.New]
              : filterState.status,
        };

        setTicketFilters(updatedFilters);

        break;
      case 1:
        if (user?.id)
          setTicketFilters({
            ...defaultFilter,
            createdByUserId: user?.id,
            ...processFilterState(filterState, defaultFilter),
            ticketStatus:
              !filterState.status || filterState.status.length === 0
                ? filterClosedAndDoneTicketStatus(
                    Object.values(FindAllTicketsQueryFiltersTicketStatusEnum)
                  )
                : filterState.status,
          });
        break;
      case 2:
        if (user?.id)
          setTicketFilters({
            ...defaultFilter,
            assignedToUserId: user?.id,
            ...processFilterState(filterState, defaultFilter),
            ticketStatus:
              !filterState.status || filterState.status.length === 0
                ? filterClosedAndDoneTicketStatus(
                    Object.values(FindAllTicketsQueryFiltersTicketStatusEnum)
                  )
                : filterState.status,
          });
        break;
      case 3:
        setTicketFilters({
          ...defaultFilter,
          ...processFilterState(filterState, defaultFilter),
          ticketStatus:
            !filterState.status || filterState.status.length === 0
              ? filterClosedAndDoneTicketStatus(
                  Object.values(FindAllTicketsQueryFiltersTicketStatusEnum)
                ).filter(
                  (status) => status !== FindAllTicketsQueryFiltersTicketStatusEnum.New //  exclude NEW only here
                )
              : filterState.status,
        });
        break;
      default:
        setTicketFilters({ ...commonFilter });
    }
  };
  // const [selectedTicket, setSelectedTicket] = useState<any>(null);

  const [selectedTicketId, setSelectedTicketId] = useState<number | undefined>();
  // const [isTicketLoading, setIsTicketLoading] = useState(false);

  const handleRowClick = async (ticket: any) => {
    // setIsTicketLoading(true);
    try {
      setOpenDialog(true);

      // const response = await getTicketById(ticket?.id);
      // setSelectedTicket(response);
      setSelectedTicketId(ticket.id);
    } catch (error) {
      console.error("Error fetching ticket:", error);
    } finally {
      // setIsTicketLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTicketId(undefined);
  };

  // --- BSNR (clinic) sort & filter ---
  const [bsnrFilter, setBsnrFilter] = useState("");
  const [debouncedBsnrFilter, setDebouncedBsnrFilter] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedBsnrFilter(bsnrFilter.trim()), 350);
    return () => clearTimeout(handle);
  }, [bsnrFilter]);

  const effectiveFilters = useMemo<FindAllTicketsQueryFilters>(
    () => ({
      ...ticketFilters,
      // When the clinic filter is active, results are always grouped/sorted by
      // clinic id (backend maps the "BSNR" sort key to rx.clinicId).
      ...(debouncedBsnrFilter
        ? { BSNR: debouncedBsnrFilter, sortBy: FindAllTicketsQueryFiltersSortByEnum.Bsnr }
        : {}),
    }),
    [ticketFilters, debouncedBsnrFilter]
  );

  const {
    data: ticketsData,
    isFetchingNextPage: isLoading,
    hasNextPage: hasMore,
    fetchNextPage,
  } = useTickets({
    filters: effectiveFilters,
    enabled: !!user?.locationId && !!ticketFilters.locationId,
  });

  useEffect(() => {
    if (user?.locationId) {
      setTicketFilters((prevFilters) => ({
        ...prevFilters,
        locationId: user?.locationId,
      }));
    }
  }, [user?.locationId]);

  const tickets = ticketsData?.pages.reduce((tickets, page) => tickets.concat(page), []);

  return (
    <>
      <Card
        // sx={{ maxHeight: "440px" }}
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "440px",
          maxHeight: "440px",
          position: "relative", // Required for absolute positioning of TicketFilters
        }}
      >
        <CardHeader avatar={<TicketIcon />} title="Tickets" />

        {/* Floating Filters - positioned absolutely so they don't take up vertical space */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          <TicketFilters
            filterState={filterState}
            showFilters={showFilters}
            onTypeChange={updateType}
            onStatusChange={updateStatus}
            onFromDateChange={updateFromDate}
            onToDateChange={updateToDate}
            onSortChange={updateSort}
            onResetFilters={resetFilters}
            onToggleFilters={toggleFilters}
            defaultSort={{
              sortBy: FindAllTicketsQueryFiltersSortByEnum.CreatedAt,
              sortOrder: FindAllTicketsQueryFiltersSortOrderEnum.Asc,
            }}
            translatedFlatFilters={{
              ...(translatedFlatTicketFilters as TicketFlatFilterDto),
              ticketType:
                translatedFlatTicketFilters?.ticketType?.filter(
                  (type) => type.value !== FindAllTicketsQueryFiltersTicketTypeEnum.OrderRx
                ) || [],
            }}
          />
        </Box>

        <CardContent
          sx={{
            height: "400px",
            position: "relative",
            flex: 1,
            minHeight: 0,
            overflow: "hidden", // Keep hidden - let TicketsTable handle the scrolling
            display: "flex",
            flexDirection: "column",
            p: 0,
            m: 0,
          }}
        >
          {/* Same tab-strip recipe as the Rezept Bestellungen card: slight
              breathing room above the tabs, afforded by compact tab-label
              padding (36px strip instead of MUI's 48px default). */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0.75,
              px: 1,
              pb: 0,
              borderBottom: 1,
              borderColor: "divider",
              minWidth: "fit-content",
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}
            >
              <Tab iconPosition="start" label="Neu" />
              <Tab iconPosition="start" label="Von mir" />
              <Tab iconPosition="start" label="Für mich" />
              <Tab iconPosition="start" label="In Bearbeitung" />
            </Tabs>
            <IconButton
              onClick={() => setOpenTicket(true)}
              sx={{ backgroundColor: mobiliTheme.palette.secondary.main }}
            >
              <Add sx={{ color: "white" }} />
            </IconButton>
          </Box>

          <BsnrTableToolbar
            bsnrFilter={bsnrFilter}
            onBsnrFilterChange={setBsnrFilter}
          />

          {!user || !tickets ? (
            <>
              <CircularProgress sx={{ alignSelf: "center", mt: 4 }} />
            </>
          ) : (
            <>
              {tickets && tickets.length ? (
                <TicketsTable
                  key={"tab-" + selectedTab}
                  handleRowClick={handleRowClick}
                  ticketsData={tickets}
                  hasMoreData={hasMore}
                  fetchMoreData={fetchNextPage}
                  isLoading={isLoading}
                  groupByClinic={effectiveFilters.sortBy === FindAllTicketsQueryFiltersSortByEnum.Bsnr}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <Typography color="text.secondary">Keine Tickets gefunden</Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <CreateTicketForm
        openTicket={openTicket}
        onCloseTicketForm={() => setOpenTicket(!openTicket)}
        refresh={refresh}
        setRefresh={setRefresh}
      />
      <TicketDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        ticketId={selectedTicketId}
        refresh={refresh}
        setRefresh={setRefresh}
        onTicketUpdate={(ticket: TicketDto) => {
          // Handle ticket update
          // onAfterTicketUpdate(ticket, user?.locationId);
        }}
      />
    </>
  );
});

export default TicketOverview;
